import { useCallback, useState } from 'react';
import { useTaskOne } from '@sib/api';
import { NO_DATA_PLACEHOLDER } from '@sib/config';
import { useTranslation } from '@sib/i18n';
import { FormMode } from '@sib/utils';
import {
  DetailsModalBody,
  DetailsModalHeader,
  DetailsModalView,
  useDetailsModalViewHandlers,
} from '@sidecar/common/components/DetailsModal';
import { TaskDetailsNavigationMode, TaskDetailsView } from '../TaskDetailsView';
import { TaskForm } from '../TaskForm';
import { TaskFormEntityContext } from '../TaskForm.utils';

interface TaskDetailsModalContentProps {
  onClose: () => void;
  taskId?: string;
  type: FormMode;
  isEdit: boolean;
  view: DetailsModalView;
  onViewChange: (view: DetailsModalView) => void;
  taskContext?: TaskFormEntityContext;
  navigationMode?: TaskDetailsNavigationMode;
  showCompletedField?: boolean;
}

export const TaskDetailsModalContent = ({
  onClose,
  taskId,
  type,
  isEdit,
  view,
  onViewChange,
  taskContext,
  navigationMode = 'route',
  showCompletedField,
}: TaskDetailsModalContentProps) => {
  const { t } = useTranslation();
  const [nextStepParentTaskId, setNextStepParentTaskId] = useState<
    string | null
  >(null);
  const { data: task, isLoading: isTaskLoading } = useTaskOne(
    isEdit ? taskId : null
  );
  const { handleFormCancel, handleEdit } = useDetailsModalViewHandlers({
    isEdit,
    onViewChange,
    onClose,
  });

  const isCreatingNextStep = !!nextStepParentTaskId;
  const formType = isCreatingNextStep ? FormMode.Create : type;

  const handleCreateNextStep = useCallback(
    (parentTaskId: string) => {
      setNextStepParentTaskId(parentTaskId);
      onViewChange(DetailsModalView.Form);
    },
    [onViewChange]
  );

  const handleCancel = useCallback(() => {
    setNextStepParentTaskId(null);
    handleFormCancel();
  }, [handleFormCancel]);

  const isDetailsView = view === DetailsModalView.Details;
  const dialogTitle = isDetailsView
    ? (task?.title ?? NO_DATA_PLACEHOLDER)
    : formType === FormMode.Edit
      ? t('components.taskFormDialog.edit')
      : t('components.taskFormDialog.new');

  return (
    <>
      <DetailsModalHeader
        title={dialogTitle}
        isTitleLoading={isDetailsView && isTaskLoading}
        onClose={onClose}
      />
      <DetailsModalBody>
        {isDetailsView ? (
          taskId && (
            <TaskDetailsView
              taskId={taskId}
              onClose={onClose}
              onEdit={handleEdit}
              onCreateNextStep={handleCreateNextStep}
              navigationMode={navigationMode}
            />
          )
        ) : (
          <TaskForm
            {...(!isCreatingNextStep && { taskId: taskId })}
            nextStepParentTaskId={nextStepParentTaskId}
            onClose={onClose}
            onCancel={handleCancel}
            cancelLabel={isEdit ? t('back') : t('cancel')}
            type={formType}
            taskContext={taskContext}
            showCompletedField={showCompletedField}
          />
        )}
      </DetailsModalBody>
    </>
  );
};
